import { createTheme, Theme } from "@fluentui/react";
import { colors, spacing, typography } from "./variables";
import "./base/index.css";

export const theme: Theme = createTheme({
  defaultFontStyle: {
    fontSize: "13px",
    fontWeight: typography.weights.regular,
    fontFamily: typography.fonts.primary,
  },
  fonts: {
    small: {
      fontSize: "13px",
      lineHeight: typography.lineHeights.small,
    },
    medium: {
      fontSize: "16px",
      lineHeight: typography.lineHeights.medium,
    },
    large: {
      fontSize: "20px",
      lineHeight: typography.lineHeights.medium,
    },
  },
  palette: {
    themePrimary: colors.primary,
    white: colors.white,
    black: colors.black,
  },
  semanticColors: {
    bodyBackground: colors.white,
    bodyStandoutBackground: colors.neutrals.n30,
    bodyText: colors.text.dark,
    errorText: colors.error[500],
    errorBackground: colors.error[50],
    errorIcon: colors.error[500],
    warningBackground: colors.warning[50],
    infoBackground: colors.info[50],
    inputBorder: "#535856",
    inputBorderHovered: "#838383",
    inputText: colors.text.dark,
    buttonBackground: "#F3F3F3",
    buttonBackgroundHovered: colors.p1[100],
    buttonBackgroundPressed: "#DDDDDD",
    buttonBackgroundDisabled: "#F3F3F3",
    primaryButtonBackgroundHovered: colors.p1[700],
    primaryButtonBackgroundPressed: colors.p1[400],
    primaryButtonBackgroundDisabled: colors.p1[200],
  },
  components: {
    PrimaryButton: {
      styles: {
        root: {
          "&.is-disabled": {
            color: colors.text.white,
          },
          "i svg path": {
            fill: `${colors.white}!important`,
          },
        },
      },
    },
    DefaultButton: {
      //Secondary button, Primary button inherits DefaultButton styles
      styles: {
        root: {
          padding: "10px 16px",
          borderRadius: "8px",
          height: "unset",
          fontSize: "16px",
          borderColor: "transparent",
          ".ms-Button-label": {
            margin: "unset",
            lineHeight: typography.lineHeights.medium,
            fontWeight: typography.weights.bold,
          },
          "&:focus::after": {
            inset: "-1px!important",
            border: `2px solid ${colors.text.dark}!important`,
            borderRadius: "12px",
          },
          "&.is-disabled": {
            color: colors.text.disabled,
          },
          "i svg path": {
            fill: `${colors.primary}!important`,
          },
        },
        flexContainer: {
          gap: "6px",
        },
      },
    },
    ActionButton: {
      styles: {
        root: {
          lineHeight: typography.lineHeights.medium,
          ":hover": {
            color: colors.p1[800],
          },
          "&.is-disabled": {
            color: colors.text.disabled,
          },
          "i svg path": {
            fill: `${colors.primary}!important`,
          },
        },
      },
    },
    TextField: {
      styles: {
        root: {
          width: "100%",
          "@media(min-width: 960px)": {
            width: 516,
          },
          ".ms-TextField-fieldGroup, .ms-TextField-field": {
            borderRadius: "4px",
            backgroundColor: colors.white,
            input: {
              borderRadius: "unset",
            },
          },

          label: {
            fontWeight: typography.weights.medium,
            paddingBottom: spacing.n,
            paddingTop: "unset",
          },
          ".ms-TextField-fieldGroup": {
            height: "unset",
            padding: "8px 10px",
            alignItems: "center",
            "::after": {
              borderRadius: "4px",
            },
            ":active": {
              borderColor: colors.primary,
            },
          },
          ".ms-TextField-reveal": {
            height: "24px!important",
          },
          ".ms-TextField-reveal:hover": {
            backgroundColor: "transparent",
          },
          ".ms-TextField-field": {
            padding: "unset",
            borderRadius: "unset",
            "&.is-disabled": {
              label: {
                color: "initial",
              },
            },
          },
        },
      },
    },
    ComboBox: {
      styles: {
        root: {
          height: "unset",
          padding: "8px 10px",
          backgroundColor: "white",
          position: "relative",
          "::after": {
            borderRadius: "4px",
          },
          ":active": {
            borderColor: colors.primary,
          },
          borderRadius: "4px",
          "&.is-disabled": {
            backgroundColor: "white",
            "::after": {
              borderRadius: "4px",
              borderColor: "#e4e4e4!important",
            },
            button: {
              visibility: "hidden",
            },
          },
          input: {
            borderRadius: "unset",
            backgroundColor: "white",
            color: "initial",
          },
          label: {
            color: "red!important",
            fontWeight: typography.weights.bold,
          },
          button: {
            backgroundColor: "white",
            position: "absolute",
            right: "0",
            ":hover": {
              backgroundColor: "white",
              cursor: "pointer",
            },
          },
        },
      },
    },
    Dropdown: {
      styles: {
        root: {
          ".ms-Dropdown": {
            width: "unset",
            borderColor: "red",
            borderRadius: "4px!important",
            "@media(min-width: 960px)": {
              width: 516,
            },
            ":focus::after": {
              borderRadius: "4px!important",
            },
          },
          ".is-disabled": {
            backgroundColor: "white",
            borderColor: "#e4e4e4!important",

            ".ms-Dropdown-title": {
              borderColor: "#e4e4e4!important",
              border: "1px solid",
            },
            i: {
              visibility: "hidden",
            },
          },

          ".ms-Dropdown-title": {
            borderRadius: "4px!important",
            padding: "10px 32px 10px 12px",
            height: "43px !important",
            lineHeight: typography.lineHeights.medium,
            background: colors.white,
            color: `${colors.text.dark}!important`,
          },
          ".ms-Dropdown-option": {
            color: colors.text,
          },
          ".ms-Dropdown-caretDownWrapper": {
            top: "5px",
          },
          ".ms-Dropdown-callout": {
            boxShadow: "0px 4px 50px rgba(0, 0, 0, 0.08)!important",
            borderRadius: "4px",
          },
        },
      },
    },
    Layer: {
      styles: {
        root: {
          ".ms-Callout": {
            boxShadow: "0px 4px 50px rgba(0, 0, 0, 0.08)!important",
            borderRadius: "12px",
          },
          ".ms-ContextualMenu-list": {
            li: {
              padding: spacing.n2,
              button: {
                borderRadius: "6px",
              },
            },
          },
        },
      },
    },
    Pivot: {
      styles: {
        root: {
          marginBottom: "1vh",
        },
      },
    },
    Modal: {
      styles: {
        root: {
          ".ms-Modal-scrollableContent": {
            overflowY: "unset",
          },
          ".ms-Dialog-main": { maxWidth: "60%" },
        },
      },
    },
  },
});
